import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Instagram } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      
      toast.success("Nachricht gesendet!", {
        description: "Ich melde mich schnellstmöglich bei dir.",
      });
      
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      // Error handling - toast notification shown to user
      toast.error("Fehler beim Senden", {
        description: "Bitte versuche es erneut.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      className="contact-section py-20 lg:py-32"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#c1ff72] font-semibold mb-4">
              Kontakt
            </p>
            <h2 className="font-['Bebas_Neue'] text-4xl md:text-6xl lg:text-7xl uppercase tracking-tighter text-white mb-6">
              SCHREIB <span className="text-[#c1ff72]">MIR</span>
            </h2>
            <p className="text-lg text-[#A1A1AA] leading-relaxed mb-10">
              Du hast Fragen zum Training oder möchtest einen individuellen 
              Termin vereinbaren? Kontaktiere mich gerne!
            </p>

            {/* Contact Details */}
            <div className="space-y-6" data-testid="contact-details">
              <a
                href="tel:+4901741944996"
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 bg-[#c1ff72]/10 flex items-center justify-center group-hover:bg-[#c1ff72]/20 transition-colors duration-300">
                  <Phone className="w-5 h-5 text-[#c1ff72]" />
                </div>
                <div>
                  <p className="text-[#71717A] text-sm">Telefon</p>
                  <p className="text-white font-medium group-hover:text-[#c1ff72] transition-colors">
                    0174 194 4996
                  </p>
                </div>
              </a>

              <a
                href="mailto:kontakt@personal-fussballtraining.de"
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 bg-[#c1ff72]/10 flex items-center justify-center group-hover:bg-[#c1ff72]/20 transition-colors duration-300">
                  <Mail className="w-5 h-5 text-[#c1ff72]" />
                </div>
                <div>
                  <p className="text-[#71717A] text-sm">E-Mail</p>
                  <p className="text-white font-medium group-hover:text-[#c1ff72] transition-colors">
                    kontakt@personal-fussballtraining.de
                  </p>
                </div>
              </a>

              <a
                href="https://instagram.com/fussballtraining_aktas"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
                data-testid="contact-instagram"
              >
                <div className="w-12 h-12 bg-[#c1ff72]/10 flex items-center justify-center group-hover:bg-[#c1ff72]/20 transition-colors duration-300">
                  <Instagram className="w-5 h-5 text-[#c1ff72]" />
                </div>
                <div>
                  <p className="text-[#71717A] text-sm">Instagram</p>
                  <p className="text-white font-medium group-hover:text-[#c1ff72] transition-colors">
                    @fussballtraining_aktas
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#c1ff72]/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#c1ff72]" />
                </div>
                <div>
                  <p className="text-[#71717A] text-sm">Trainingsregion</p>
                  <p className="text-white font-medium">Meinerzhagen, Plettenberg & Drolshagen</p>
                </div>
              </div>
            </div>

          </div>

          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Label className="text-[#A1A1AA] mb-2 block">Name *</Label>
                <Input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Dein Name"
                  className="bg-[#1A1A1A] border-white/10 text-white h-12 rounded-none focus:border-[#c1ff72]"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <Label className="text-[#A1A1AA] mb-2 block">E-Mail *</Label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="deine@email.de"
                  className="bg-[#1A1A1A] border-white/10 text-white h-12 rounded-none focus:border-[#c1ff72]"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <Label className="text-[#A1A1AA] mb-2 block">Telefon</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+49 123 456789"
                  className="bg-[#1A1A1A] border-white/10 text-white h-12 rounded-none focus:border-[#c1ff72]"
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <Label className="text-[#A1A1AA] mb-2 block">Nachricht *</Label>
                <Textarea
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Deine Nachricht..."
                  rows={6}
                  className="bg-[#1A1A1A] border-white/10 text-white rounded-none resize-none focus:border-[#c1ff72]"
                  data-testid="contact-message-textarea"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#1A1A1A] font-semibold py-6 rounded-none transition-all duration-300 disabled:opacity-50 hover:shadow-[0_0_30px_rgba(193,255,114,0.5)]"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  "WIRD GESENDET..."
                ) : (
                  <>
                    NACHRICHT SENDEN
                    <Send className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
